// KDS Demo - Simulates orders flowing through the workflow system
import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  Settings, 
  Zap,
  Clock,
  Users,
  TrendingUp
} from 'lucide-react';

interface DemoOrder {
  id: string;
  customer_name: string;
  services: string[];
  priority: number;
  created_at: Date;
  current_station: string;
  needs_stain_removal: boolean;
  needs_alteration: boolean;
}

const KDSDemo: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [orders, setOrders] = useState<DemoOrder[]>([]);
  const [speed, setSpeed] = useState(2); // Orders per minute
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const sampleOrders = [
    {
      customer_name: "John Smith",
      services: ["Dry Cleaning", "Pressing"],
      priority: 1,
      needs_stain_removal: false,
      needs_alteration: false
    },
    {
      customer_name: "Sarah Johnson",
      services: ["Stain Removal", "Dry Cleaning"],
      priority: 2,
      needs_stain_removal: true,
      needs_alteration: false
    },
    {
      customer_name: "Mike Wilson",
      services: ["Alterations", "Dry Cleaning", "Pressing"],
      priority: 1,
      needs_stain_removal: false,
      needs_alteration: true
    },
    {
      customer_name: "Emily Davis",
      services: ["Express Cleaning"],
      priority: 3,
      needs_stain_removal: false,
      needs_alteration: false
    },
    {
      customer_name: "Robert Brown",
      services: ["Stain Removal", "Alterations", "Pressing"],
      priority: 2,
      needs_stain_removal: true,
      needs_alteration: true
    },
    {
      customer_name: "Lisa Garcia",
      services: ["Dry Cleaning"],
      priority: 1,
      needs_stain_removal: false,
      needs_alteration: false
    },
    {
      customer_name: "David Miller",
      services: ["Wedding Dress Cleaning"],
      priority: 2,
      needs_stain_removal: true,
      needs_alteration: false
    },
    {
      customer_name: "Jennifer Lee",
      services: ["Suit Cleaning", "Pressing"],
      priority: 1,
      needs_stain_removal: false,
      needs_alteration: false
    }
  ];

  const stations = [
    { id: 'processing', name: 'Processing', color: '#3B82F6' },
    { id: 'stain_removal', name: 'Stain Removal', color: '#EF4444' },
    { id: 'alteration', name: 'Alterations', color: '#8B5CF6' },
    { id: 'wash_dry', name: 'Wash & Dry', color: '#10B981' },
    { id: 'fold_press', name: 'Fold & Press', color: '#F59E0B' },
    { id: 'pickup', name: 'Pickup', color: '#06B6D4' },
    { id: 'delivery', name: 'Delivery', color: '#84CC16' },
    { id: 'completed', name: 'Completed', color: '#6B7280' }
  ];

  const createNewOrder = () => {
    const template = sampleOrders[Math.floor(Math.random() * sampleOrders.length)];
    const newOrder: DemoOrder = {
      id: `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...template,
      created_at: new Date(),
      current_station: 'processing'
    };
    
    setOrders(prev => [...prev, newOrder]);
  };

  const advanceOrder = (orderId: string) => {
    setOrders(prev => prev.map(order => {
      if (order.id !== orderId) return order;

      let nextStation = '';
      
      switch (order.current_station) {
        case 'processing':
          if (order.needs_stain_removal) {
            nextStation = 'stain_removal';
          } else if (order.needs_alteration) {
            nextStation = 'alteration';
          } else {
            nextStation = 'wash_dry';
          }
          break;
        case 'stain_removal':
          nextStation = order.needs_alteration ? 'alteration' : 'wash_dry';
          break;
        case 'alteration':
          nextStation = 'wash_dry';
          break;
        case 'wash_dry':
          nextStation = 'fold_press';
          break;
        case 'fold_press':
          nextStation = Math.random() > 0.7 ? 'delivery' : 'pickup';
          break;
        case 'pickup':
        case 'delivery':
          nextStation = 'completed';
          setTotalProcessed(prev => prev + 1);
          break;
        default:
          return order;
      }

      return { ...order, current_station: nextStation };
    }).filter(order => order.current_station !== 'completed'));
  };

  const resetDemo = () => {
    setOrders([]);
    setTotalProcessed(0);
    setAverageTime(0);
    setIsRunning(false);
  };

  // Auto-create orders
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      createNewOrder();
    }, (60 / speed) * 1000); // Convert orders per minute to milliseconds

    return () => clearInterval(interval);
  }, [isRunning, speed]);

  // Auto-advance orders
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      setOrders(prev => {
        const ordersToAdvance = prev.filter(() => Math.random() > 0.6); // 40% chance to advance
        ordersToAdvance.forEach(order => {
          setTimeout(() => advanceOrder(order.id), Math.random() * 2000);
        });
        return prev;
      });
    }, 3000); // Check every 3 seconds

    return () => clearInterval(interval);
  }, [isRunning]);

  // Calculate average processing time
  useEffect(() => {
    if (orders.length > 0) {
      const now = new Date();
      const avgTime = orders.reduce((sum, order) => {
        const timeInSystem = (now.getTime() - order.created_at.getTime()) / 1000 / 60; // minutes
        return sum + timeInSystem;
      }, 0) / orders.length;
      setAverageTime(avgTime);
    }
  }, [orders]);

  const getStationCount = (stationId: string) => {
    return orders.filter(order => order.current_station === stationId).length;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Demo Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">KDS Workflow Demo</h1>
            <p className="text-gray-600">
              Watch orders flow through the complete dry cleaning workflow system
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Speed Control */}
            <div className="flex items-center space-x-2">
              <Settings className="w-4 h-4 text-gray-500" />
              <label className="text-sm text-gray-600">Speed:</label>
              <select 
                value={speed} 
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value={1}>1 order/min</option>
                <option value={2}>2 orders/min</option>
                <option value={4}>4 orders/min</option>
                <option value={6}>6 orders/min</option>
              </select>
            </div>
            
            {/* Control Buttons */}
            <button
              onClick={() => setIsRunning(!isRunning)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isRunning 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{isRunning ? 'Pause Demo' : 'Start Demo'}</span>
            </button>
            
            <button
              onClick={resetDemo}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Demo Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Orders</p>
              <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{totalProcessed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Time</p>
              <p className="text-2xl font-bold text-gray-900">{averageTime.toFixed(1)}m</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Zap className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Throughput</p>
              <p className="text-2xl font-bold text-gray-900">{speed}/min</p>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Visualization */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Live Workflow Status</h2>
        
        <div className="flex items-center justify-between overflow-x-auto pb-4">
          {stations.slice(0, -1).map((station, index) => {
            const count = getStationCount(station.id);
            
            return (
              <React.Fragment key={station.id}>
                <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                  <div 
                    className={`p-6 rounded-full mb-3 relative ${
                      count > 0 ? 'ring-4 ring-offset-2 animate-pulse' : ''
                    }`}
                    style={{ 
                      backgroundColor: `${station.color}20`,
                      color: station.color,
                      borderColor: count > 0 ? station.color : undefined
                    }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <span className="text-lg font-bold">{count}</span>
                    </div>
                    
                    {count > 0 && (
                      <div 
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: station.color }}
                      >
                        {count}
                      </div>
                    )}
                  </div>
                  
                  <span className="text-sm font-medium text-gray-900 text-center">{station.name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full mt-1 ${
                    count > 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {count} orders
                  </span>
                </div>
                
                {index < stations.length - 2 && (
                  <div className="flex-shrink-0 mx-4">
                    <div className="w-12 h-0.5 bg-gray-300 relative">
                      {count > 0 && (
                        <div 
                          className="h-0.5 bg-current animate-pulse"
                          style={{ 
                            width: '100%',
                            color: station.color
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Active Orders List */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Active Orders</h2>
        
        {orders.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No active orders. Start the demo to see orders flowing through the system!</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto orders-scroll">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Customer</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Services</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Priority</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Current Station</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Time in System</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const station = stations.find(s => s.id === order.current_station);
                  const timeInSystem = (new Date().getTime() - order.created_at.getTime()) / 1000 / 60;
                  
                  return (
                    <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{order.customer_name}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {order.services.map((service, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {service}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          order.priority === 3 ? 'bg-red-100 text-red-800' :
                          order.priority === 2 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {order.priority === 3 ? 'Rush' : order.priority === 2 ? 'Urgent' : 'Normal'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: station?.color }}
                        >
                          {station?.name}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {timeInSystem.toFixed(1)}m
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => advanceOrder(order.id)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                        >
                          Advance
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default KDSDemo;
